import { DeleteIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormLabel,
    Grid,
    GridItem,
    HStack,
    IconButton,
    Input,
    SimpleGrid,
    useColorModeValue
} from '@chakra-ui/react';
import { useState } from 'react';
import { translate } from '../../../i18n';
import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { CourtScheduleRuleDto, CreateCourtConfigurationDto } from '../../../models/court/dto/configuration.dto';
import PriceInput, { isPriceInputValid } from '../../court-pricing-management/components/PriceInput';
import { parsePrice } from '../../../utils/parse-price';

type CreateCourtConfigurationFormProps = {
    onSubmit: (data: Omit<CreateCourtConfigurationDto, 'courtId'>) => void;
    onCancel: () => void;
    isLoading: boolean;
};

export const CreateCourtConfigurationForm = ({ onSubmit, onCancel, isLoading }: CreateCourtConfigurationFormProps) => {
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const [isAppliedToAllTime, setIsAppliedToAllTime] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [rules, setRules] = useState<Omit<CourtScheduleRuleDto, 'id'>[]>([
        {
            price: 0,
            priceDuration: 60,
            startTime: new Date('1970-01-01T09:00:00.000Z').toISOString(),
            endTime: new Date('1970-01-01T17:00:00.000Z').toISOString(),
            daysOfWeekUTC: [1, 2, 3, 4, 5],
            currency: Currency.EUR
        }
    ]);

    const handleSubmit = () => {
        if (!isAppliedToAllTime && (!startDate || !endDate)) return;
        if (rules.length === 0) return;

        onSubmit({
            isAppliedToAllTime,
            startDate: startDate ? new Date(startDate).toISOString() : null,
            endDate: endDate ? new Date(endDate).toISOString() : null,
            rules
        });
    };

    return (
        <Box>
            <FormControl mb={4}>
                <FormLabel>
                    <Checkbox
                        isChecked={isAppliedToAllTime}
                        onChange={(e) => setIsAppliedToAllTime(e.target.checked)}
                    >
                        {translate('court_configuration.form.is_applied_to_all_time')}
                    </Checkbox>
                </FormLabel>
            </FormControl>

            {!isAppliedToAllTime && (
                <SimpleGrid columns={2} spacing={4} mb={4}>
                    <FormControl>
                        <FormLabel>{translate('court_configuration.form.start_date')}</FormLabel>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel>{translate('court_configuration.form.end_date')}</FormLabel>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </FormControl>
                </SimpleGrid>
            )}

            <Box mb={4}>
                <HStack justify="space-between" align="center" mb={4}>
                    <FormLabel mb={0}>{translate('court_configuration.form.rules')}</FormLabel>
                    <Button
                        colorScheme="brand"
                        size="sm"
                        onClick={() => {
                            setRules([
                                ...rules,
                                {
                                    price: 0,
                                    priceDuration: 60,
                                    startTime: new Date('1970-01-01T09:00:00.000Z').toISOString(),
                                    endTime: new Date('1970-01-01T17:00:00.000Z').toISOString(),
                                    daysOfWeekUTC: [1, 2, 3, 4, 5],
                                    currency: Currency.EUR
                                }
                            ]);
                        }}
                    >
                        {translate('court_configuration.form.add_rule')}
                    </Button>
                </HStack>
                {rules.map((rule, index) => (
                    <Grid
                        key={index}
                        templateColumns="1.2fr 1fr 1fr 1fr 40px"
                        gap={4}
                        mb={4}
                        p={4}
                        border="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        alignItems="flex-end"
                    >
                        <GridItem>
                            <FormControl isInvalid={!isPriceInputValid(rule.price.toString())}>
                                <FormLabel>{translate('court_configuration.form.price')}</FormLabel>
                                <PriceInput
                                    name={`price-${index}`}
                                    value={rule.price}
                                    onChange={(e) => {
                                        const newRules = [...rules];
                                        newRules[index].price = parsePrice(e.target.value);
                                        setRules(newRules);
                                    }}
                                    currency={rule.currency}
                                    setCurrency={(newCurrency) => {
                                        const newRules = [...rules];
                                        newRules[index].currency = newCurrency;
                                        setRules(newRules);
                                    }}
                                />
                            </FormControl>
                        </GridItem>
                        <GridItem>
                            <FormControl>
                                <FormLabel>{translate('court_configuration.form.price_duration')}</FormLabel>
                                <Input
                                    type="number"
                                    value={rule.priceDuration}
                                    onChange={(e) => {
                                        const newRules = [...rules];
                                        newRules[index].priceDuration = Number(e.target.value);
                                        setRules(newRules);
                                    }}
                                />
                            </FormControl>
                        </GridItem>
                        <GridItem>
                            <FormControl>
                                <FormLabel>{translate('court_configuration.form.start_time')}</FormLabel>
                                <Input
                                    type="time"
                                    value={new Date(rule.startTime).toTimeString().slice(0, 5)}
                                    onChange={(e) => {
                                        const newRules = [...rules];
                                        const [hours, minutes] = e.target.value.split(':');
                                        const date = new Date('1970-01-01');
                                        date.setHours(Number(hours), Number(minutes));
                                        newRules[index].startTime = date.toISOString();
                                        setRules(newRules);
                                    }}
                                />
                            </FormControl>
                        </GridItem>
                        <GridItem>
                            <FormControl>
                                <FormLabel>{translate('court_configuration.form.end_time')}</FormLabel>
                                <Input
                                    type="time"
                                    value={new Date(rule.endTime).toTimeString().slice(0, 5)}
                                    onChange={(e) => {
                                        const newRules = [...rules];
                                        const [hours, minutes] = e.target.value.split(':');
                                        const date = new Date('1970-01-01');
                                        date.setHours(Number(hours), Number(minutes));
                                        newRules[index].endTime = date.toISOString();
                                        setRules(newRules);
                                    }}
                                />
                            </FormControl>
                        </GridItem>
                        <GridItem>
                            <FormControl>
                                <FormLabel visibility="hidden">Action</FormLabel>
                                <IconButton
                                    marginTop={-4}
                                    aria-label="Delete rule"
                                    icon={<DeleteIcon />}
                                    colorScheme="red"
                                    size="sm"
                                    onClick={() => {
                                        const newRules = [...rules];
                                        newRules.splice(index, 1);
                                        setRules(newRules);
                                    }}
                                />
                            </FormControl>
                        </GridItem>
                    </Grid>
                ))}
            </Box>

            <HStack spacing={4} justify="flex-end">
                <Button variant="ghost" onClick={onCancel}>
                    {translate('cancel')}
                </Button>
                <Button
                    colorScheme="brand"
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    isDisabled={rules.length === 0 || (!isAppliedToAllTime && (!startDate || !endDate))}
                >
                    {translate('save')}
                </Button>
            </HStack>
        </Box>
    );
};